import { useTheme } from "@mui/material";
import { MonthDescription } from "../../api/archive";

// Daily capture calendar for one year, laid out wide: 12 month ROWS × up to
// 31 day COLUMNS (day-of-month). Each cell is a day, shaded by that day's
// page volume (own quantile scale); a day inside a covered month with no
// pages is RED (a real capture gap); a day in a month with no cached
// description at all is light gray ("no data"); non-existent days (Feb 30)
// are blank. Native <title> gives a hover tooltip. The wide orientation
// fills horizontal space and lets you scan a given day-of-month down every
// month. Dependency-free SVG, scales to its container width.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Map month "01".."12" → that month's description (or undefined if not cached).
function byMonth(months: MonthDescription[]): Map<number, MonthDescription> {
  const m = new Map<number, MonthDescription>();
  for (const d of months) m.set(Number(d.meta.month), d);
  return m;
}

function ymd(year: string, monthNum: number, day: number): string {
  return `${year}-${String(monthNum).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

// Quantile-ish bucket (0..4) of a day's pages against the year's max, so a
// few huge days don't wash everything else out (sqrt compresses the top).
function bucket(pages: number, max: number): number {
  if (pages <= 0 || max <= 0) return 0;
  const f = Math.sqrt(pages) / Math.sqrt(max);
  return Math.min(4, 1 + Math.floor(f * 4));
}

export function YearCalendar({ year, months }: { year: string; months: MonthDescription[] }) {
  const theme = useTheme();
  const monthMap = byMonth(months);

  // Year-wide max daily pages → the shared shade scale.
  let maxDaily = 1;
  for (const d of months) {
    for (const v of Object.values(d.coverage?.per_day_pages ?? {})) {
      if (v > maxDaily) maxDaily = v;
    }
  }

  // Five shades of the primary hue (bucket 0 unused → covered-but-zero is red).
  const shades = [
    theme.palette.action.hover, // 0 (unused for present days)
    theme.palette.primary.light,
    theme.palette.primary.main,
    theme.palette.primary.dark,
    theme.palette.primary.dark,
  ];
  const RED = theme.palette.error.main;
  const NODATA = theme.palette.action.disabledBackground;

  // Layout — wide: day-of-month across (31 cols), months down (12 rows).
  const cell = 26;
  const gap = 4;
  const step = cell + gap;
  const labelTop = 16; // day-of-month labels across the top
  const labelLeft = 34; // month name labels down the left
  const W = labelLeft + 31 * step;
  const H = labelTop + 12 * step;

  type Day = { x: number; y: number; fill: string; tip: string };
  const days: Day[] = [];

  for (let mi = 0; mi < 12; mi++) {
    const monthNum = mi + 1;
    const doc = monthMap.get(monthNum);
    const y = labelTop + mi * step;
    const dim = doc?.coverage?.days_in_month ?? new Date(Number(year), monthNum, 0).getDate();
    const per = doc?.coverage?.per_day_pages ?? {};

    for (let day = 1; day <= dim; day++) {
      const x = labelLeft + (day - 1) * step;
      const key = ymd(year, monthNum, day);
      if (!doc) {
        days.push({ x, y, fill: NODATA, tip: `${key} · no cached description` });
        continue;
      }
      const pages = per[key] ?? 0;
      if (pages > 0) {
        days.push({
          x,
          y,
          fill: shades[bucket(pages, maxDaily)],
          tip: `${key} · ${pages.toLocaleString()} pages`,
        });
      } else {
        // In a cached month but no pages that day → a real capture gap.
        days.push({ x, y, fill: RED, tip: `${key} · no capture` });
      }
    }
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ maxWidth: W, height: "auto", display: "block" }}
        role="img"
        aria-label={`${year} daily capture`}
      >
        {/* day-of-month labels across the top (1, then every 5th, + 31) */}
        {Array.from({ length: 31 }, (_, i) => i + 1)
          .filter((d) => d === 1 || d % 5 === 0 || d === 31)
          .map((d) => (
            <text
              key={d}
              x={labelLeft + (d - 1) * step + cell / 2}
              y={11}
              fontSize={9}
              textAnchor="middle"
              fill={theme.palette.text.disabled}
            >
              {d}
            </text>
          ))}
        {/* month labels down the left */}
        {MONTHS.map((m, mi) => (
          <text
            key={m}
            x={labelLeft - 6}
            y={labelTop + mi * step + cell / 2 + 3}
            fontSize={10}
            textAnchor="end"
            fill={theme.palette.text.secondary}
          >
            {m}
          </text>
        ))}
        {/* day cells */}
        {days.map((d, i) => (
          <rect key={i} x={d.x} y={d.y} width={cell} height={cell} rx={3} fill={d.fill}>
            <title>{d.tip}</title>
          </rect>
        ))}
      </svg>
    </div>
  );
}
